import { React, useState, useEffect, useContext } from 'react';
import { Button, Grid, TextField, Typography, MenuItem, FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { useHistory } from 'react-router-dom';
import { useUrlContext } from '../context/urlContext';
import { useAuth } from '../context/authContext';
import { errorContext } from '../Components/ErrorDisplay';

function normalizeMobileInput(value, selectedCountryCode) {
    if (!value) return null;

    const digits = value.replace(/\D/g, '');
    const countryCode = String(selectedCountryCode || '').replace(/\D/g, '');

    let localNumber = digits;

    if (countryCode === '91') {
        if (localNumber.startsWith('91')) {
            if (localNumber.length === 12) {
                localNumber = localNumber.slice(2);
            } else {
                return null;
            }
        }

        if (localNumber.startsWith('0') && localNumber.length === 11) {
            localNumber = localNumber.slice(1);
        }

        if (!/^[6789]\d{9}$/.test(localNumber)) return null;

        return { countryCode: '91', localNumber, fullNumber: `+91${localNumber}` };
    }

    if (countryCode === '61') {
        if (localNumber.startsWith('61') && localNumber.length >= 11) {
            localNumber = localNumber.slice(2);
        }

        if (/^4\d{8}$/.test(localNumber)) {
            localNumber = `0${localNumber}`;
        }

        if (!/^04\d{8}$/.test(localNumber)) return null;

        return { countryCode: '61', localNumber, fullNumber: `+61${localNumber.slice(1)}` };
    }

    if (countryCode === '1') {
        if (localNumber.startsWith('1') && localNumber.length === 11) {
            localNumber = localNumber.slice(1);
        }

        if (!/^[2-9]\d{2}[2-9]\d{6}$/.test(localNumber)) return null;

        return { countryCode: '1', localNumber, fullNumber: `+1${localNumber}` };
    }

    return null;
}

function resolveDialCode(rawDialCode) {
    const code = String(rawDialCode || '').replace(/\D/g, '');
    if (code === '91' || code === '61' || code === '1') return code;
    return '61';
}

// Used twice, by two routes differing only in which promotion type they offer
// and their heading:
//   Single Member   — promotionType 2 (Member offer)
//   Single Consumer — promotionType 1 (Public promotion)
// Parameterised rather than copied, so a coupon fix is made once.
export default function GrantSingleMemberForm({
    promotionType,
    heading,
}) {
    const history = useHistory();
    const theme = useTheme();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const [message, setMessage] = useState(null);
    const [isErrorMessage, setIsErrorMessage] = useState(false);
    const [promotionList, setPromotionList] = useState([]);
    const [gettingPromotions, setGettingPromotions] = useState(false);
    // Whether /scp has answered for the current member — distinct from "the list
    // has entries". Filtering to one promotion type makes an empty-but-fetched
    // result ordinary, and it must not be reported as "not fetched yet".
    const [promotionsFetched, setPromotionsFetched] = useState(false);
    const { UrlHelperObj } = useUrlContext();
    const { user } = useAuth();
    const { handleError } = useContext(errorContext);

    const validationSchema = yup.object({
        member_identifier: yup
            .string('AAA')
            .required('Value is required')
            .test('member-id-or-mobile-by-type', function(value) {
                if (!value) {
                    return this.createError({ message: 'Value is required' });
                }

                const trimmed = value.trim();
                const selectedType = this.parent.member_input_type;
                const selectedCountryCode = this.parent.mobile_country_code;
                console.log("Validating member identifier '"+value+"' with type '"+selectedType+"' and country code '"+selectedCountryCode+"'");

                if (selectedType === 'mobile_number') {
                    const normalizedCode = resolveDialCode(selectedCountryCode);
                    const isMobile = normalizeMobileInput(trimmed, normalizedCode) !== null;
                    if (!isMobile) {
                        return this.createError({ message: 'Enter valid mobile number' });
                    }
                    return true;
                }

                const isMemberId = /^[A-Za-z0-9_-]+$/.test(trimmed);
                if (!isMemberId) {
                    return this.createError({ message: 'Enter a valid Member ID' });
                }
                return true;
            }),
        member_input_type: yup
            .string('AAA')
            .required('Please select input type'),
        mobile_country_code: yup
            .string('AAA')
            .when('member_input_type', {
                is: 'mobile_number',
                then: yup.string('AAA').required('Please select country')
            }),
        promotion_id: yup
            .string('AAA')
            .required('Please select coupon')
    });

    const formik = useFormik({
        enableReinitialize: true,
        validateOnMount: true,
        validationSchema: validationSchema,
        initialValues: {
            member_identifier: '',
            member_input_type: 'member_id',
            mobile_country_code: '61',
            promotion_id: ''
        },
        onSubmit: async (values) => {
            try {
                const inputValue = values.member_identifier.trim();
                const memberInputType = values.member_input_type;
                let memberValue = inputValue;

                if (memberInputType === 'mobile_number') {
                    const selectedCode = resolveDialCode(values.mobile_country_code);
                    const normalized = normalizeMobileInput(inputValue, selectedCode);
                    if (!normalized) {
                        formik.setFieldTouched('member_identifier', true, false);
                        formik.setFieldError('member_identifier', 'Enter valid mobile number');
                        return;
                    }
                    memberValue = normalized.fullNumber;
                }

                await axios.post(UrlHelperObj.unknownmemberpassUrl(), {
                    issuer_id: user.issuerId,
                    promotion_id: values.promotion_id,
                    member_input_type: memberInputType,
                    member_value: memberValue
                });
                setIsErrorMessage(false);
                setMessage('Issue request submitted successfully.');
            } catch (err) {
                handleError(err);
                const responseErrorMessage = err?.response?.data?.message || err?.response?.data?.error || err?.response?.error;
                const finalErrorMessage = responseErrorMessage || err?.message || 'Failed to submit grant request.';
                setIsErrorMessage(true);
                setMessage(finalErrorMessage);
            }
        }
    });

    useEffect(() => {
        if (message !== null) {
            setMessage(null);
            setIsErrorMessage(false);
        }
    }, [formik.values.member_identifier, formik.values.member_input_type, formik.values.mobile_country_code, formik.values.promotion_id]);

    // When the member changes, previously fetched promotions no longer apply.
    useEffect(() => {
        setPromotionList([]);
        setPromotionsFetched(false);
        formik.setFieldValue('promotion_id', '', false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formik.values.member_identifier, formik.values.member_input_type, formik.values.mobile_country_code]);

    // Fetch the promotions valid for the entered member via POST /scp.
    async function getPromotions() {
        const inputValue = (formik.values.member_identifier || '').trim();
        const memberInputType = formik.values.member_input_type;
        let memberValue = inputValue;

        if (memberInputType === 'mobile_number') {
            const selectedCode = resolveDialCode(formik.values.mobile_country_code);
            const normalized = normalizeMobileInput(inputValue, selectedCode);
            if (!normalized) {
                formik.setFieldTouched('member_identifier', true, false);
                formik.setFieldError('member_identifier', 'Enter valid mobile number');
                return;
            }
            memberValue = normalized.fullNumber;
        } else if (!/^[A-Za-z0-9_-]+$/.test(inputValue)) {
            formik.setFieldTouched('member_identifier', true, false);
            formik.setFieldError('member_identifier', 'Enter a valid Member ID');
            return;
        }

        setGettingPromotions(true);
        try {
            const response = await axios.post(UrlHelperObj.scpUrl(), {
                issuer_id: user.issuerId,
                member_input_type: memberInputType,
                member_value: memberValue
            });
            const data = response.data || {};
            // The server's message is surfaced by the global response interceptor;
            // promotions are only available when error === 0.
            const promos = (Number(data.error) === 0 && Array.isArray(data.promotions)) ? data.promotions : [];
            // /scp returns every coupon valid for this member, across types. One
            // type per page: Member offer (2) for membership coupons, Public
            // promotion (1) for consumer coupon passes. Filtered here rather
            // than server-side so a missing `type` fails CLOSED — an empty list
            // is visibly wrong, whereas showing the other kind would let someone
            // issue the wrong coupon with nothing looking amiss.
            setPromotionList(promos.filter(p => Number(p.type) === Number(promotionType)));
            // Answered — even if the filtered list came back empty.
            setPromotionsFetched(true);
            formik.setFieldValue('promotion_id', '', false);
        } catch (err) {
            setPromotionList([]);
        setPromotionsFetched(false);
            formik.setFieldValue('promotion_id', '', false);
            handleError(err);
        } finally {
            setGettingPromotions(false);
        }
    }

    // Switching between member ID / mobile clears the field so the new input
    // starts fresh and editable (avoids the previous value carrying over).
    function handleInputTypeChange(e) {
        formik.setFieldValue('member_input_type', e.target.value, false);
        formik.setFieldValue('member_identifier', '', false);
        formik.setFieldValue('mobile_country_code', '61', false);
        formik.setFieldValue('promotion_id', '', false);
        formik.setFieldTouched('member_identifier', false, false);
        setPromotionList([]);
        setPromotionsFetched(false);
    }

    // Let the user unlock the member field to enter a different member/mobile.
    function resetMemberInput() {
        setPromotionList([]);
        setPromotionsFetched(false);
        formik.setFieldValue('promotion_id', '', false);
    }

    function onCancelForm() {
        history.push(user?.issuerId ? `/club/${user.issuerId}/home` : '/');
    }

    // Once promotions are loaded they belong to the entered member, so the member
    // input and type are locked until the user clicks "Change".
    const promotionsLoaded = promotionList.length > 0;
    const hasPromotion = Boolean(formik.values.promotion_id);
    const hasMemberIdentifier = Boolean(formik.values.member_identifier && formik.values.member_identifier.trim());
    const selectedDialCode = resolveDialCode(formik.values.mobile_country_code);
    const isMemberIdentifierValid = formik.values.member_input_type === 'mobile_number'
        ? normalizeMobileInput(formik.values.member_identifier || '', selectedDialCode) !== null
        : /^[A-Za-z0-9_-]+$/.test((formik.values.member_identifier || '').trim());
    const hasValidationErrors = Object.keys(formik.errors).length > 0;
    const isSubmitDisabled = !hasPromotion || !hasMemberIdentifier || !isMemberIdentifierValid || hasValidationErrors || formik.isSubmitting;

    return (
        <form onSubmit={formik.handleSubmit} noValidate>
            <Grid container item xs={12} spacing={2} direction="column" alignItems='flex-start' style={{ borderStyle: 'none', borderColor: 'black', marginTop: 0, marginLeft: 10 }}>
                <Grid item xs={12} style={{ width: '100%' }}>
                    <Typography
                        variant={isSmallScreen ? 'h6' : 'h5'}
                        component="h1"
                        style={{
                            textAlign: 'left',
                            marginTop: 0,
                            marginBottom: '12px',
                            fontWeight: 400,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.3
                        }}
                    >
                        {heading}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    <FormControl component="fieldset" error={formik.touched.member_input_type && Boolean(formik.errors.member_input_type)}>
                        <RadioGroup
                            row={!isSmallScreen}
                            id="member_input_type"
                            name="member_input_type"
                            value={formik.values.member_input_type}
                            onChange={handleInputTypeChange}
                            onBlur={formik.handleBlur}
                        >
                            <FormControlLabel value="member_id" disabled={promotionsLoaded} control={<Radio color="primary" />} label="Enter member ID" />
                            <FormControlLabel value="mobile_number" disabled={promotionsLoaded} control={<Radio color="primary" />} label="Enter mobile number" />
                        </RadioGroup>
                        <Typography variant="caption" color="error">
                            {formik.touched.member_input_type && formik.errors.member_input_type ? formik.errors.member_input_type : ''}
                        </Typography>
                    </FormControl>
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    {formik.values.member_input_type === 'mobile_number' ? (
                        <>
                            <PhoneInput
                                inputProps={{
                                    name: 'member_identifier',
                                    required: true
                                }}
                                required
                                disabled={promotionsLoaded}
                                value={formik.values.member_identifier}
                                countryCodeEditable={false}
                                containerStyle={{ width: '100%' }}
                                inputStyle={{ width: '100%', height: '56px' }}
                                copyNumbersOnly={false}
                                country={{ '1': 'us', '61': 'au', '91': 'in' }[resolveDialCode(formik.values.mobile_country_code)]}
                                onlyCountries={['au', 'in', 'us']}
                                onChange={(phone, ctr) => {
                                    const nextCountryCode = resolveDialCode(ctr?.dialCode || formik.values.mobile_country_code);
                                    formik.setFieldValue('mobile_country_code', nextCountryCode, false);
                                    formik.setFieldValue('member_identifier', phone || '', true);
                                    formik.setFieldTouched('member_identifier', true, false);
                                    formik.setFieldTouched('mobile_country_code', true, false);
                                }}
                                onBlur={() => {
                                    formik.setFieldTouched('member_identifier', true);
                                    formik.setFieldTouched('mobile_country_code', true);
                                }}
                            />
                            <Typography variant="caption" color="error">
                                {formik.touched.member_identifier && formik.errors.member_identifier ? formik.errors.member_identifier : ''}
                            </Typography>
                        </>
                    ) : (
                        <TextField
                            required
                            fullWidth
                            disabled={promotionsLoaded}
                            variant='outlined'
                            id="member_identifier"
                            name="member_identifier"
                            label="Member ID"
                            placeholder='e.g. 20012345687'
                            value={formik.values.member_identifier}
                            onBlur={formik.handleBlur}
                            onChange={formik.handleChange}
                            error={formik.touched.member_identifier && Boolean(formik.errors.member_identifier)}
                            helperText={formik.touched.member_identifier && formik.errors.member_identifier}
                        />
                    )}
                </Grid>

                <Grid item xs={12} sm={8} md={6} style={{ width: '100%', maxWidth: 480 }}>
                    {promotionsLoaded ? (
                        <Button
                            type='button'
                            variant='outlined'
                            onClick={resetMemberInput}
                            style={{ minWidth: isSmallScreen ? 200 : 'auto' }}
                        >
                            Change member / mobile
                        </Button>
                    ) : (
                        <Button
                            type='button'
                            variant='contained'
                            onClick={getPromotions}
                            disabled={gettingPromotions || !isMemberIdentifierValid}
                            style={{ minWidth: isSmallScreen ? 200 : 'auto' }}
                        >
                            {gettingPromotions ? 'Getting…' : 'Get Coupons'}
                        </Button>
                    )}
                    <Typography variant="caption" style={{ display: 'block', marginTop: 6, color: '#555' }}>
                        {promotionsLoaded
                            ? 'Showing coupons for the entered member. Click "Change member / mobile" to enter a different one.'
                            : 'Enter the member ID or mobile number, then click "Get Coupons" to load the coupons available to that member.'}
                    </Typography>
                </Grid>

                <Grid item xs={12} sm={6} md={4} style={{ width: '100%', maxWidth: 480 }}>
                    <TextField
                        required
                        fullWidth
                        variant='outlined'
                        select
                        id="promotion_id"
                        name="promotion_id"
                        label="Select Coupon"
                        value={formik.values.promotion_id}
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        disabled={promotionList.length === 0}
                        error={formik.touched.promotion_id && Boolean(formik.errors.promotion_id)}
                        helperText={
                            (formik.touched.promotion_id && formik.errors.promotion_id)
                            // The empty-state answer is NOT here — as helper text
                            // it was small grey caption type under a greyed-out
                            // select, and read as decoration. It gets its own
                            // line below instead.
                            || (!promotionsFetched && promotionList.length === 0
                                ? 'Click "Get Coupons" to load the list'
                                : '')
                        }
                    >
                        {promotionList.map((promotion) => (
                            <MenuItem key={promotion.id} value={promotion.id}>
                                {promotion.name}
                            </MenuItem>
                        ))}
                    </TextField>
                    {/* Fetched but empty is a real answer, not a missing step.
                        Sized and coloured to be noticed: the user has just
                        clicked a button and needs to know it worked and found
                        nothing, rather than assuming the page is broken. */}
                    {promotionsFetched && promotionList.length === 0 ? (
                        <Typography
                            sx={{
                                mt: 1,
                                fontSize: '1rem',
                                fontWeight: 600,
                                color: 'warning.dark',
                            }}
                        >
                            No coupons available.
                        </Typography>
                    ) : null}
                </Grid>
            </Grid>

            {message !== null && (
                <Grid container item xs={12} style={{ borderStyle: 'none', borderColor: 'black' }}>
                    <Grid item xs={12} style={{ textAlign: 'center', marginTop: '10px' }}>
                        <Typography color={isErrorMessage ? 'error' : 'primary'}>{message}</Typography>
                    </Grid>
                </Grid>
            )}

            <Grid container item xs={12}
                direction={isSmallScreen ? 'column' : 'row'} spacing={isSmallScreen ? 1 : 3}
                style={{ borderStyle: 'none', borderColor: 'black', marginLeft: 10 }} justifyContent='flex-start'>
                <Grid item>
                    <Button
                        type='submit'
                        disabled={isSubmitDisabled}
                        variant='contained'
                        style={{
                            minWidth: isSmallScreen ? 200 : 'auto',
                            backgroundColor: isSubmitDisabled ? '#9e9e9e' : '#1976d2',
                            color: isSubmitDisabled ? '#424242' : '#ffffff',
                            opacity: 1,
                            boxShadow: isSubmitDisabled ? 'none' : undefined,
                            cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                            filter: isSubmitDisabled ? 'grayscale(30%)' : 'none'
                        }}
                    >
                        Submit
                    </Button>
                </Grid>
                <Grid item>
                    <Button type='button' style={{ minWidth: isSmallScreen ? 200 : 'auto' }} onClick={onCancelForm} color='secondary' variant='contained'>Cancel</Button>
                </Grid>
            </Grid>
        </form>
    );
}
